/**
 * Bundle tier picker.
 * Lets a shopper pick a "buy N, save X%" tier. Selecting a tier sets the
 * quantity used by the existing product form (Dawn's native quantity input
 * if present, otherwise a hidden fallback input this script creates) and
 * updates the displayed unit total / savings. It does not create a discount
 * itself — the actual price reduction at checkout comes from a matching
 * Shopify Automatic Discount configured in Admin. See the theme editor
 * "info" text on each tier block for details.
 */
class BundleTierPicker extends HTMLElement {
  constructor() {
    super();
    this.unitPrice = parseInt(this.dataset.unitPrice || '0', 10);
    this.currencyFormat = this.dataset.moneyFormat || '{{amount}}';
  }

  connectedCallback() {
    this.tiers = Array.from(this.querySelectorAll('.bundle-tier'));
    this.form = this.closest('form') || document.getElementById(this.dataset.formId);
    this.quantityInput = this.getQuantityInput();

    this.tiers.forEach((tier) => {
      tier.addEventListener('click', () => this.selectTier(tier));
      tier.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectTier(tier);
        }
      });
    });

    const preselected = this.querySelector('.bundle-tier[data-highlight="true"]') || this.tiers[0];
    if (preselected) this.selectTier(preselected, true);
  }

  getQuantityInput() {
    const formId = this.dataset.formId;
    let input = formId ? document.querySelector(`#${formId} input[name="quantity"]`) : null;
    if (!input && this.form) {
      input = this.form.querySelector('input[name="quantity"]');
    }
    if (!input && this.form) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = '1';
      this.form.appendChild(input);
    }
    return input;
  }

  selectTier(tier, silent) {
    this.tiers.forEach((t) => {
      t.classList.remove('bundle-tier--selected');
      t.setAttribute('aria-checked', 'false');
    });
    tier.classList.add('bundle-tier--selected');
    tier.setAttribute('aria-checked', 'true');

    const quantity = parseInt(tier.dataset.quantity || '1', 10);
    const discount = parseFloat(tier.dataset.discount || '0');

    if (this.quantityInput) {
      this.quantityInput.value = quantity;
      if (!silent) {
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.updateSummary(quantity, discount);
    this.dispatchEvent(new CustomEvent('bundle:tier-selected', {
      bubbles: true,
      detail: { quantity, discount },
    }));
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.currencyFormat.replace('{{amount}}', amount);
  }

  updateSummary(quantity, discountPercent) {
    const summary = this.querySelector('.bundle-tier-picker__summary');
    if (!summary || !this.unitPrice) return;

    const fullTotal = this.unitPrice * quantity;
    const discountedTotal = Math.round(fullTotal * (1 - discountPercent / 100));
    const savings = fullTotal - discountedTotal;

    const totalEl = summary.querySelector('[data-bundle-total]');
    const compareEl = summary.querySelector('[data-bundle-compare]');
    const savingsEl = summary.querySelector('[data-bundle-savings]');

    if (totalEl) totalEl.textContent = this.formatMoney(discountedTotal);

    if (compareEl) {
      if (discountPercent > 0) {
        compareEl.textContent = this.formatMoney(fullTotal);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    if (savingsEl) {
      if (discountPercent > 0) {
        savingsEl.textContent = savingsEl.dataset.template
          .replace('[amount]', this.formatMoney(savings))
          .replace('[percent]', discountPercent);
        savingsEl.hidden = false;
      } else {
        savingsEl.hidden = true;
      }
    }
  }
}

if (!customElements.get('bundle-tier-picker')) {
  customElements.define('bundle-tier-picker', BundleTierPicker);
}
